/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * ExifService.js
 *
 * Extracts EXIF metadata from photos using ExifReader
 * Handles fractional rational focal length values and common mobile device fallbacks
 *
 * Pipeline Step 3a: EXIF Extraction
 * Input: Image file (Blob/File/URL)
 * Output: Focal length and hardware metadata
 */

import ExifReader from 'exifreader'
import { getDefaultFocalLength } from '@/utils/ml/useFocalLength.js'
import focalLengthPresetsData from '@/constants/focalLengthPresets.json'

/**
 * Fallback focal length values for common devices (in mm)
 */
const FALLBACK_FOCAL_LENGTHS = {
  default: () => getDefaultFocalLength(),
  iPhone: 4.25,
  Samsung: 4.3,
  'Google Pixel': 4.4,
  Huawei: 4.0,
}

/**
 * Service for extracting and managing EXIF data from images
 */
export class ExifService {
  constructor() {
    // Don't cache fallback (always get fresh from settings)
  }

  /**
   * Get current fallback focal length from user settings
   */
  getFallbackFocalLength() {
    return getDefaultFocalLength()
  }

  /**
   * Extract camera settings from photo metadata
   * Gets focal length and sensor size if available
   *
   * @param {Blob|File|ArrayBuffer|string} imageSource - Image to extract from
   * @returns {Object} Camera metadata (focal length, sensor size, etc.)
   */
  async extractExifData(imageSource) {
    try {
      // Convert to ArrayBuffer for ExifReader
      let arrayBuffer
      if (imageSource instanceof ArrayBuffer) {
        arrayBuffer = imageSource
      } else if (imageSource instanceof Blob || imageSource instanceof File) {
        arrayBuffer = await imageSource.arrayBuffer()
      } else if (typeof imageSource === 'string') {
        if (imageSource.startsWith('data:')) {
          const base64 = imageSource.split(',')[1]
          const binaryString = atob(base64)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          arrayBuffer = bytes.buffer
        } else {
          const response = await fetch(imageSource)
          arrayBuffer = await response.arrayBuffer()
        }
      }

      // Attempt to load tags first (crucial for HEIC and when Image() fails)
      let tags = null
      try {
        tags = ExifReader.load(arrayBuffer)
      } catch (err) {
        console.warn('[EXIF] ExifReader failed to load buffer:', err.message)
      }

      // get dimensions
      let dimensions = { width: 0, height: 0 }
      try {
        dimensions = await this.getImageDimensions(imageSource)
      } catch (error) {
        console.warn(
          '[EXIF] getImageDimensions failed (likely HEIC/unsupported format):',
          error.message
        )
        // Fallback to EXIF-based dimensions if available
        if (tags) {
          dimensions.width =
            tags['Image Width']?.value || tags['PixelXDimension']?.value || 0
          dimensions.height =
            tags['Image Height']?.value || tags['PixelYDimension']?.value || 0
        }
      }

      if (!tags) {
        return this.createFallbackExifData(dimensions)
      }

      // 1. Physical Focal Length (0x920A)
      let focalLength = null
      if (tags.FocalLength) {
        const val = tags.FocalLength.value
        focalLength = Array.isArray(val) ? val[0] / val[1] : val
      }

      // 2. 35mm Equivalent (0xA405)
      let focal35mm = tags.FocalLengthIn35mmFilm?.value || null

      // Android Fallback 1: Calculate 35mm equivalent from Focal Plane Resolution
      if (
        !focal35mm &&
        focalLength &&
        tags.FocalPlaneXResolution &&
        tags.FocalPlaneYResolution
      ) {
        try {
          const unit = tags.FocalPlaneResolutionUnit?.value || 2 // 2=inch, 3=cm
          const factor = unit === 3 ? 10 : 25.4
          const resX = tags.FocalPlaneXResolution.value
          const resY = tags.FocalPlaneYResolution.value
          const rX = Array.isArray(resX) ? resX[0] / resX[1] : resX
          const rY = Array.isArray(resY) ? resY[0] / resY[1] : resY

          const imgW =
            tags['Image Width']?.value ||
            tags['PixelXDimension']?.value ||
            dimensions.width
          const imgH =
            tags['Image Height']?.value ||
            tags['PixelYDimension']?.value ||
            dimensions.height

          if (rX > 0 && rY > 0 && imgW > 0 && imgH > 0) {
            const sensorWidth = (imgW / rX) * factor
            const sensorHeight = (imgH / rY) * factor
            const sensorDiagonal = Math.sqrt(
              sensorWidth * sensorWidth + sensorHeight * sensorHeight
            )
            focal35mm = (43.27 * focalLength) / sensorDiagonal
            console.log(
              '[EXIF] Derived 35mm equivalent from focal plane:',
              focal35mm
            )
          }
        } catch (e) {
          console.warn('[EXIF] Focal plane calculation failed:', e)
        }
      }

      // Android Fallback 2: Try to match a known preset by focal length
      if (!focal35mm && focalLength) {
        const matchingPreset = focalLengthPresetsData.find(
          (p) => Math.abs(p.focalLength - focalLength) < 0.05
        )
        if (matchingPreset) {
          focal35mm = matchingPreset.focal35mm
          console.log('[EXIF] Matched 35mm equivalent from preset:', focal35mm)
        }
      }

      // check for common manufacturer tags if still missing
      if (!focalLength && tags.MakerNote) {
        // Todo: Implement manufacturer-specific parsing if needed
      }

      return {
        focalLength: focalLength,
        focalLengthIn35mm: focal35mm,
        focal35mm: focal35mm,
        make: tags.Make?.description || null,
        model: tags.Model?.description || null,
        imageWidth: dimensions.width,
        imageHeight: dimensions.height,
        rawWidth:
          tags['Image Width']?.value ||
          tags['PixelXDimension']?.value ||
          dimensions.width,
        rawHeight:
          tags['Image Height']?.value ||
          tags['PixelYDimension']?.value ||
          dimensions.height,
        orientation: tags.Orientation?.value || 1,
        dateTime: tags.DateTime?.description || null,
        gpsLatitude: tags.GPSLatitude?.description || null,
        gpsLongitude: tags.GPSLongitude?.description || null,
        hasExifData: true,
        hasFocalLength: focalLength !== null && focal35mm !== null, // only true if set complete
      }
    } catch (error) {
      console.warn('[EXIF] Extraction error:', error.message)
      const dimensions = await this.getImageDimensions(imageSource).catch(
        () => ({ width: 0, height: 0 })
      )
      return this.createFallbackExifData(dimensions)
    }
  }

  /**
   * Get focal length with fallback
   *
   * @param {Blob|File|string} imageSource - Image to extract from
   * @returns {number} Focal length in mm (never null)
   */
  async getFocalLength(imageSource) {
    const exifData = await this.extractExifData(imageSource)

    if (exifData.focalLength !== null) {
      return exifData.focalLength
    }

    // Try device-specific fallback
    if (exifData.model) {
      const fallback = this.getFallbackForDevice(exifData.model)
      if (fallback !== null) {
        console.log(
          `Using device-specific fallback: ${fallback}mm for ${exifData.model}`
        )
        return fallback
      }
    }

    const fallback = this.getFallbackFocalLength()
    console.log(`Using default fallback focal length: ${fallback}mm`)
    return fallback
  }

  /**
   * Get device-specific fallback focal length
   */
  getFallbackForDevice(deviceModel) {
    const model = deviceModel.toLowerCase()

    for (const [key, value] of Object.entries(FALLBACK_FOCAL_LENGTHS)) {
      if (model.includes(key.toLowerCase())) {
        // Handle function values (like 'default')
        return typeof value === 'function' ? value() : value
      }
    }

    return null
  }

  /**
   * Create fallback EXIF data
   */
  createFallbackExifData(dimensions) {
    return {
      focalLength: null,
      focalLengthIn35mm: null,
      focal35mm: null,
      make: null,
      model: null,
      imageWidth: dimensions.width,
      imageHeight: dimensions.height,
      // Include raw dimensions for orientation detection compatibility
      rawWidth: dimensions.width,
      rawHeight: dimensions.height,
      orientation: 1,
      dateTime: new Date(),
      gpsLatitude: null,
      gpsLongitude: null,
      hasExifData: false,
      hasFocalLength: false,
    }
  }

  /**
   * Get image dimensions
   */
  async getImageDimensions(imageSource) {
    return new Promise((resolve, reject) => {
      const img = new Image()

      img.onload = () => {
        resolve({ width: img.width, height: img.height })
        if (typeof imageSource !== 'string') {
          URL.revokeObjectURL(img.src)
        }
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      if (typeof imageSource === 'string') {
        img.src = imageSource
      } else {
        img.src = URL.createObjectURL(imageSource)
      }
    })
  }

  /**
   * Check if image has EXIF data
   */
  async hasExifData(imageSource) {
    const exif = await this.extractExifData(imageSource)
    return exif.hasExifData
  }

  /**
   * Get 35mm equivalent focal length
   */
  async get35mmEquivalentFocalLength(imageSource) {
    const exif = await this.extractExifData(imageSource)
    return exif.focalLengthIn35mm
  }
}
